import { useState, useMemo, useCallback } from 'react';
import { 
  FEE_STRUCTURES, 
  EBAY_SELLER_LEVELS, 
  EBAY_STORE_TYPES, 
  OVERSEAS_SALES, 
  calculateSellerLevelAdjustment,
  calculateInternationalFee
} from '../utils/calculator.ts';

// Types
type Country = 'US' | 'CA';
type Category = string;
type SellerLevel = string;
type OverseasSales = string;

interface CalculatorState {
  cost: string;
  salePrice: string;
  salesTax: string;
  country: Country;
  category: Category;
  finalValueFee: number;
  insertionFee: number;
  totalEbayFee: number;
  shippingCharges: string;
  shippingCost: string;
  quantity: string;
  sellerLevel: SellerLevel;
  overseasSales: OverseasSales;
  totalFee: number | null;
  sellerLevelAdjustment: number;
  internationalFee: number;
}

interface CalculatorReturn extends CalculatorState {
  grossProfit: number;
  profitMargin: number;
  salesTaxAmount: number;
  setSalePrice: (value: string) => void;
  setCost: (value: string) => void;
  setCountry: (country: Country) => void;
  setCategory: (category: Category) => void;
  setOverseasSales: (overseasSales: OverseasSales) => void;
  setSellerLevel: (sellerLevel: SellerLevel) => void;
  setShippingCharges: (shippingCharges: string) => void;
  setShippingCost: (shippingCost: string) => void;
  setQuantity: (quantity: string) => void;
  handleQuantityBlur: () => void;
  setSalesTax: (salesTax: string) => void;
}

function useEbayFeeCalculator(initialCountry: Country): CalculatorReturn {
  const initialCategory = Object.keys(FEE_STRUCTURES[initialCountry])[0];
  const initialFinalValueFee = FEE_STRUCTURES[initialCountry][initialCategory].finalValueFee(0);
  const initialInsertionFee = FEE_STRUCTURES[initialCountry][initialCategory].insertionFee;
  const initialTotalEbayFee = initialFinalValueFee + initialInsertionFee;

  const [state, setState] = useState<CalculatorState>({
    cost: "",
    salePrice: "",
    salesTax: "0",
    country: initialCountry,
    category: initialCategory,
    finalValueFee: initialFinalValueFee,
    insertionFee: initialInsertionFee,
    totalEbayFee: initialTotalEbayFee,
    shippingCharges: "",
    shippingCost: "",
    quantity: "1",
    sellerLevel: EBAY_SELLER_LEVELS[0],
    overseasSales: OVERSEAS_SALES[initialCountry][0],
    totalFee: null,
    sellerLevelAdjustment: 0.0,
    internationalFee: 0.0
  });

  const calculateFees = useCallback((state: CalculatorState) => {
    const quantityNum = parseInt(state.quantity) || 0;
    const salePriceNum = parseFloat(state.salePrice) || 0;
    const shippingChargesNum = parseFloat(state.shippingCharges) || 0;
    const shippingCostNum = parseFloat(state.shippingCost) || 0;
    const salesTaxNum = parseFloat(state.salesTax) || 0;
    
    // If no valid sale price, return minimal fees (just insertion fee)
    if (salePriceNum <= 0) {
      let insertionFee = FEE_STRUCTURES[state.country][state.category].insertionFee * quantityNum;
      return { 
        insertionFee, 
        finalValueFee: 0, 
        totalEbayFee: insertionFee, 
        sellerLevelAdjustment: 0, 
        internationalFee: 0 
      };
    }
    
    let insertionFee = FEE_STRUCTURES[state.country][state.category].insertionFee * quantityNum;
    
    // Special case for athletic shoes: free insertion fee if starting price is $150 or more
    if (state.category.includes("Athletic Shoes") && salePriceNum >= 150) {
      insertionFee = 0;
    }
    
    // Calculate total sale amount including sales tax and shipping charges (this is what eBay bases final value fee on)
    const totalSaleAmount = (salePriceNum + (salesTaxNum / 100 * salePriceNum) + shippingChargesNum) * quantityNum;
    
    // Calculate final value fee based on total sale amount (including sales tax and shipping charges)
    const finalValueFee = FEE_STRUCTURES[state.country][state.category].finalValueFee(totalSaleAmount);
    
    // Calculate seller level adjustment
    const sellerLevelAdjustment = calculateSellerLevelAdjustment(state.sellerLevel, finalValueFee);
    
    // Calculate international fee based on total sale amount
    const internationalFee = calculateInternationalFee(state.overseasSales, totalSaleAmount);
    
    // Total eBay fees
    const totalEbayFee = finalValueFee + insertionFee + sellerLevelAdjustment + internationalFee;
    
    return { insertionFee, finalValueFee, totalEbayFee, sellerLevelAdjustment, internationalFee };
  }, []);

  const handleQuantityChange = useCallback(({ quantity }: { quantity: string }) => {
    setState(prevState => {
      const newState = { ...prevState, quantity };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]);

  const handleQuantityBlur = useCallback(() => {
    setState(prevState => {
      // Ensure quantity is a valid number, default to 1 if invalid
      const quantityNum = parseInt(prevState.quantity) || 1;
      const newState = { ...prevState, quantity: quantityNum.toString() };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]);

  // Helper function to check if a value is a valid positive number
  const isValidNumber = (value: string): boolean => {
    const num = parseFloat(value);
    return value !== '' && !isNaN(num) && num >= 0;
  };

  // Generic handler for numeric input fields
  const handleNumericInputChange = useCallback((field: keyof Pick<CalculatorState, 'cost' | 'salePrice' | 'shippingCharges' | 'shippingCost'>) => {
    return (value: string) => {
      setState(prevState => {
        const newState = { ...prevState, [field]: value };
        // Calculate fees if we have a valid salePrice (required for fee calculations)
        if (isValidNumber(newState.salePrice)) {
          const fees = calculateFees(newState);
          return { ...newState, ...fees };
        }
        return newState;
      });
    };
  }, [calculateFees]);

  const handleSalePriceChange = useCallback(({ salePrice }: { salePrice: string }) => {
    handleNumericInputChange('salePrice')(salePrice);
  }, [handleNumericInputChange]);

  const handleCostChange = useCallback(({ cost }: { cost: string }) => {
    handleNumericInputChange('cost')(cost);
  }, [handleNumericInputChange]);

  const handleShippingChargesChange = useCallback(({ shippingCharges }: { shippingCharges: string }) => {
    handleNumericInputChange('shippingCharges')(shippingCharges);
  }, [handleNumericInputChange]);

  const handleShippingCostChange = useCallback(({ shippingCost }: { shippingCost: string }) => {
    handleNumericInputChange('shippingCost')(shippingCost);
  }, [handleNumericInputChange]);

  const handleCountryChange = useCallback(({ country }: { country: Country }) => {
    setState(prevState => {
      const newState = { 
        ...prevState, 
        country,
        category: Object.keys(FEE_STRUCTURES[country])[0],
        overseasSales: OVERSEAS_SALES[country][0]
      };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]);

  const handleCategoryChange = useCallback(({ category }: { category: Category }) => {
    setState(prevState => { 
      const newState = { ...prevState, category };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]);

  const handleOverseasSalesChange = useCallback(({ overseasSales }: { overseasSales: OverseasSales }) => {
    setState(prevState => {
      const newState = { ...prevState, overseasSales };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]);

  const handleSellerLevelChange = useCallback(({ sellerLevel }: { sellerLevel: SellerLevel }) => {
    setState(prevState => {
      const newState = { ...prevState, sellerLevel };
      const fees = calculateFees(newState);
      return { ...newState, ...fees };
    });
  }, [calculateFees]); 

  const handleSalesTaxChange = useCallback(({ salesTax }: { salesTax: string }) => {
    setState(prevState => {
      const newState = { ...prevState, salesTax };
      // Recalculate fees when sales tax changes if we have a valid salePrice
      if (isValidNumber(newState.salePrice)) {
        const fees = calculateFees(newState);
        return { ...newState, ...fees };
      }
      return newState;
    });
  }, [calculateFees]);
        
  const salesTaxAmount = useMemo(() => {
    const quantityNum = parseInt(state.quantity) || 0;
    const price = (parseFloat(state.salePrice) || 0) * quantityNum;
    const taxRate = parseFloat(state.salesTax) || 0;
    return price * (taxRate / 100);
  }, [state.salePrice, state.salesTax, state.quantity]);

  const grossProfit = useMemo(() => {
    const quantityNum = parseInt(state.quantity) || 0;
    const price = parseFloat(state.salePrice) || 0;
    const cost = parseFloat(state.cost) || 0;
    const shippingCharges = parseFloat(state.shippingCharges) || 0;
    const shippingCost = parseFloat(state.shippingCost) || 0;
    const totalEbayFee = parseFloat(state.totalEbayFee.toString()) || 0;
    
    // If no valid sale price, return 0 (no profit)
    if (price <= 0) {
      return 0;
    }
    
    // Note: salesTaxAmount is already calculated and included in the totalEbayFee calculation
    // so we don't need to subtract it again here
    return (price + shippingCharges - totalEbayFee - cost - shippingCost) * quantityNum;
  }, [state.salePrice, state.totalEbayFee, state.cost, state.quantity, state.shippingCharges, state.shippingCost]);

  const profitMargin = useMemo(() => {
    const quantityNum = parseInt(state.quantity) || 0;
    const price = (parseFloat(state.salePrice) || 0) * quantityNum;
    const margin = price > 0 ? (grossProfit / price) * 100 : 0;
    return margin;
  }, [grossProfit, state.quantity, state.salePrice]);

  return {
    ...state,
    grossProfit,
    profitMargin,
    salesTaxAmount,
    setSalePrice: (value: string) => handleSalePriceChange({ salePrice: value }),
    setCost: (value: string) => handleCostChange({ cost: value }),
    setCountry: (country: Country) => handleCountryChange({ country }),
    setCategory: (category: Category) => handleCategoryChange({ category }),
    setOverseasSales: (overseasSales: OverseasSales) => handleOverseasSalesChange({ overseasSales }),
    setSellerLevel: (sellerLevel: SellerLevel) => handleSellerLevelChange({ sellerLevel }),
    setShippingCharges: (shippingCharges: string) => handleShippingChargesChange({ shippingCharges }),
    setShippingCost: (shippingCost: string) => handleShippingCostChange({ shippingCost }),
    setQuantity: (quantity: string) => handleQuantityChange({ quantity }),
    handleQuantityBlur,
    setSalesTax: (salesTax: string) => handleSalesTaxChange({ salesTax }),
  };
}

export default useEbayFeeCalculator;
